import React, { useRef } from 'react';

// Interactive radial (elliptical) mask overlay. Geometry is normalized 0..1 in
// the space of the framed (post-crop) image the parent exactly covers — the same
// space the GLSL preview and the ffmpeg `geq` bake use, so the handles map 1:1
// to what's rendered. The caller owns the mask and persists it; we only clamp.
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const CORNERS = [
  { id: 'nw', sx: -1, sy: -1 },
  { id: 'ne', sx: 1, sy: -1 },
  { id: 'sw', sx: -1, sy: 1 },
  { id: 'se', sx: 1, sy: 1 },
];

export default function MaskBox({ mask, onChange, selected, onSelect }) {
  const overlayRef = useRef(null);
  if (!mask) return null;

  const ptNorm = (e) => {
    const r = overlayRef.current.getBoundingClientRect();
    return {
      x: clamp((e.clientX - r.left) / r.width, 0, 1),
      y: clamp((e.clientY - r.top) / r.height, 0, 1),
    };
  };
  const drag = (e, onMove) => {
    e.preventDefault();
    e.stopPropagation();
    const move = (ev) => onMove(ptNorm(ev));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onBodyDown = (e) => {
    onSelect?.();
    const start = ptNorm(e);
    const orig = { cx: mask.cx, cy: mask.cy };
    drag(e, (p) => {
      onChange({ cx: clamp(orig.cx + (p.x - start.x), 0, 1), cy: clamp(orig.cy + (p.y - start.y), 0, 1) });
    });
  };
  const onCornerDown = (e, c) => {
    onSelect?.();
    drag(e, (p) => {
      onChange({
        rx: clamp(Math.abs(p.x - mask.cx), 0.03, 0.95),
        ry: clamp(Math.abs(p.y - mask.cy), 0.03, 0.95),
      });
    });
  };

  // --- Linear gradient overlay: a center point + an axis handle that sets the
  // direction (angle) and the transition width (feather). The shaded side shows
  // where alpha = 1 (the affected half). Mirrors the GLSL linear math.
  if (mask.type === 'linear') {
    const ang = ((Number(mask.angle) || 0) * Math.PI) / 180;
    const fw = Math.max(Number(mask.feather) || 0.001, 0.02);
    const ax = Math.cos(ang); const ay = Math.sin(ang);
    const endX = clamp(mask.cx + ax * fw, 0, 1);
    const endY = clamp(mask.cy + ay * fw, 0, 1);
    const onAxisDown = (e) => {
      onSelect?.();
      drag(e, (p) => {
        const dx = p.x - mask.cx; const dy = p.y - mask.cy;
        const dist = clamp(Math.sqrt(dx * dx + dy * dy), 0.03, 0.9);
        onChange({ angle: (Math.atan2(dy, dx) * 180) / Math.PI, feather: dist });
      });
    };
    return (
      <div ref={overlayRef} className="cs-maskbox-overlay">
        <svg className="cs-maskline-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <line
            x1={mask.cx * 100} y1={mask.cy * 100} x2={endX * 100} y2={endY * 100}
            className={`cs-maskline${selected ? ' selected' : ''}`}
          />
          <line
            x1={(mask.cx - ay * 0.5) * 100} y1={(mask.cy + ax * 0.5) * 100}
            x2={(mask.cx + ay * 0.5) * 100} y2={(mask.cy - ax * 0.5) * 100}
            className={`cs-maskline-perp${selected ? ' selected' : ''}`}
          />
        </svg>
        <span
          className={`cs-maskline-pt center${selected ? ' selected' : ''}`}
          style={{ left: `${mask.cx * 100}%`, top: `${mask.cy * 100}%` }}
          onPointerDown={onBodyDown}
        />
        {selected && (
          <span
            className="cs-maskline-pt axis"
            style={{ left: `${endX * 100}%`, top: `${endY * 100}%` }}
            onPointerDown={onAxisDown}
          />
        )}
      </div>
    );
  }

  const inner = clamp(1 - (mask.feather == null ? 0.6 : mask.feather), 0, 1);
  const box = {
    left: `${(mask.cx - mask.rx) * 100}%`,
    top: `${(mask.cy - mask.ry) * 100}%`,
    width: `${mask.rx * 2 * 100}%`,
    height: `${mask.ry * 2 * 100}%`,
  };

  return (
    <div ref={overlayRef} className="cs-maskbox-overlay">
      <div
        className={`cs-maskbox${selected ? ' selected' : ''}`}
        style={box}
        onPointerDown={onBodyDown}
      >
        {/* feathered core (alpha = full inside this dashed ellipse) */}
        <span
          className="cs-maskbox-core"
          style={{
            left: `${(1 - inner) * 50}%`,
            top: `${(1 - inner) * 50}%`,
            width: `${inner * 100}%`,
            height: `${inner * 100}%`,
          }}
        />
        {selected && CORNERS.map((c) => (
          <span
            key={c.id}
            className={`cs-maskbox-grip cs-maskbox-grip-${c.id}`}
            onPointerDown={(e) => onCornerDown(e, c)}
          />
        ))}
      </div>
    </div>
  );
}
